import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { AxiosResponse } from 'axios';
import { TFunction } from 'i18next';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import { isMobileDevice } from '../../../../shared/helpers/isMobileDevice';
import { FileUtils } from '../../../../shared/utils/FileUtils';
import { JsPDFUtils } from '../../../../shared/utils/JsPDFUtils';
import { SubmissionService } from '../../../../user/services/SubmissionService';
import { Form } from '../../../models/Form';
import { FormQuestion } from '../../../models/FormQuestion';
import { RequirementResults } from '../../../models/RequirementResults';
import { FileService } from '../../../services/FileService';
import { FormService } from '../../../services/FormService';
import { FormUtils } from '../../../utils/FormUtils';

export class FormResultExportFilesService {
  constructor(
    private readonly formService: FormService,
    private readonly t: TFunction,
    private readonly submissionService: SubmissionService,
    private readonly fileService: FileService,
  ) {}

  exportXLSX(
    xlsxData : any[],
    form: Form,
    history: number[],
    values: Record<string, any>) {
    console.log("exportXLSX")
    const workbook = utils.book_new();

    // = Summary
    const summaryData = [
      [this.formService.t(this.t, "ui_summary_date", false), this._getTodayDate()],
      [this.formService.t(this.t, "ui_summary_question", false), this.formService.t(this.t, "ui_summary_response", false)],
    ];

    this._createSummaryTable(
      summaryData,
      form,
      history,
      values
    );

    const summaryWs = utils.aoa_to_sheet(summaryData);
    utils.book_append_sheet(workbook, summaryWs, this.formService.t(this.t, "ui_summary_title", false));

    // = Requirements
    const requirementsWs = utils.aoa_to_sheet(xlsxData);
    utils.book_append_sheet(
      workbook,
      requirementsWs,
      this.formService.t(this.t, "ui_requirement_results_title", false)
    );

    // = Disclaimer
    const disclaimerWs = utils.aoa_to_sheet([[this.formService.t(this.t, "disclaimer_text", false).replace('<br>', '\n')]]);
    utils.book_append_sheet(
      workbook,
      disclaimerWs,
      this.formService.t(this.t, "disclaimer_title", false)
    );

    if (isMobileDevice()) {
      this.formService.sendXLSX(form.form, workbook).then((response: AxiosResponse<RequirementResults>) => {
        if (Capacitor.isNativePlatform()) {
          Browser.open({ url: `${ process.env.REACT_APP_API_URL }/` + response.data['link'] });
        }
        else {
          window.open(`${ process.env.REACT_APP_API_URL }/` + response.data['link'], '_blank', 'location=no');
        }

      });
    }
    else {
      writeFile(workbook, "results.xlsx");
    }
  }

  async exportPDF(
    fromWord: boolean = false,
    htmlTable,
    idSubmittedSubmission,
    language,
    setLoadingPDF,
    form: Form,
    history: number[],
    values: Record<string, any>
  ) {
    // Document configured via backend
    if (htmlTable) {

      // From docx
      const element = document.getElementById("htmlTemplate")

      if (element) {
        setLoadingPDF(true);

        if (fromWord) {
          try {
            await this.submissionService.exportSubmissionToWord(idSubmittedSubmission!, language)
              .then((response: AxiosResponse<Blob>) => {
                this.fileService.convertDocxToPdf(response.data)
                  .then((res) => {
                    FileUtils.downloadFile(res)
                    setLoadingPDF(false);
                })
            })
            setLoadingPDF(false);
          }
          catch (e) {
            console.log(e)
            setLoadingPDF(false);
          }
        }
        else {
          let clientWidth = 0;
          let mainContainer;

          // if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
          if (element) {
            clientWidth = element.clientWidth;
            if (form.pdfWidth) {
              element.style.width = form.pdfWidth + "px";
            }
            else {
              element.style.width = "1200px";
            }

            mainContainer = element.querySelector('.maincontainer');

            if (mainContainer) {
              mainContainer.classList.add('print');
            }
            // element.style.width = "1702px";
          }
          // }

          const pdf = new jsPDF('p', 'pt', 'a4');

          const htmlToConvert = document.createElement("div")
          htmlToConvert.innerHTML = (document.getElementById("htmlTemplate") as HTMLElement).innerHTML
          if (form.pdfWidth) {
            htmlToConvert.style.width = form.pdfWidth + "px";
          }
          else {
            htmlToConvert.style.width = "1200px";
          }

          pdf.html(htmlToConvert, {
            y: 0,
            x: 0,
            autoPaging:'text',
            margin: 50,
            html2canvas: {
              scale: 0.5,
              svgRendering: true,
              scrollX: 0,
              scrollY: 0
            },
            callback: () => {
              if (element) {
                element.style.width = clientWidth + "px";

                if (mainContainer) {
                  mainContainer.classList.remove('print');
                }
              }

              if (isMobileDevice()) {

                this.formService.sendPDF(form.form, pdf.output('blob')).then((response: AxiosResponse<RequirementResults>) => {
                  if (Capacitor.isNativePlatform()) {
                    Browser.open({ url: `${ process.env.REACT_APP_API_URL }/` + response.data['link'] });
                  }
                  else {
                    window.open(`${ process.env.REACT_APP_API_URL }/` + response.data['link'], '_blank', 'location=no');
                  }
                });
                // if(element) {
                //   element.style.width = clientWidth+"px";
                // }
                setLoadingPDF(false);
              }
              else {
                setLoadingPDF(false);
                pdf.save("results.pdf");
              }
            }
          });
        }

      }

      return;
    }

    // Default behavior
    JsPDFUtils.initJsPDF();

    const doc = new jsPDF();
    doc.setFont('Roboto-Bold', 'normal');
    doc.setFontSize(10);

    const headerHeight = 10;

    const langImage = new Image();
    langImage.src = this.formService.t(this.t, 'bbri_logo', false, false, false, true);
    doc.addImage(langImage, 'png', 13, 10, headerHeight * 4, headerHeight);

    const logoImage = new Image();
    logoImage.src = this.formService.t(this.t, 'logo_app', false, false, false, true);
    const logoImageRapport = logoImage.width > logoImage.height ? logoImage.width / logoImage.height : logoImage.height / logoImage.width;
    if (logoImageRapport > 2) {
      doc.addImage(logoImage, 'png', 210 - 5 * headerHeight, 10, (headerHeight) * logoImageRapport, headerHeight);
    }
    else {
      doc.addImage(logoImage, 'png', 210 - 5 * headerHeight, 10, (headerHeight) / logoImageRapport, headerHeight);
    }

    doc.setFontSize(7);
    doc.text('Version 1 - 15.01.21', 210 - 5 * headerHeight, headerHeight + 12);
    doc.setFontSize(10);

    autoTable(doc, {
      startY: headerHeight + 20,
      body: [
        [
          `${ this.formService.t(this.t, "ui_pdf_date", false) } : ${ this._getTodayDate() }`,
        ],
      ]
    });

    // = Summary
    doc.text(this.formService.t(this.t, "ui_summary_title", false), 13, (doc as any).autoTable.previous.finalY + 10);
    const summaryTable: any = {
      head: [
        [
          this.formService.t(this.t, "ui_summary_question", false),
          this.formService.t(this.t, "ui_summary_response", false)
        ]
      ],
      body: [],
      startY: (doc as any).autoTable.previous.finalY + 15,
      headStyles: {
        font: 'Roboto-Bold',
        fontStyle: 'normal'
      },
      styles: {
        font: 'Roboto-Regular',
        fontStyle: 'normal'
      }
    };

    this._createSummaryTable(
      summaryTable.body,
      form,
      history,
      values
    );

    autoTable(doc, summaryTable);

    // = Requirements
    doc.text(
      this.formService.t(this.t, "ui_requirement_results_title", false),
      13,
      (doc as any).autoTable.previous.finalY + 10
    );


    const body: string[][][] = [];
    const myData = document.getElementById("table-results") as HTMLTableElement;
    const myDataRows = myData.rows as any;
    for (const myDataRow of myDataRows) {
      if (myDataRow.cells.length === 3) {
        let el1 = myDataRow.cells[0].innerText;
        let el2 = myDataRow.cells[1].innerText.replace(/<\/?[^>]+(>|$)/g, "");
        let el3 = myDataRow.cells[2].innerHTML
          .replace(/<\/span>/g, '\n\n')
          .replace(/<li>/g, '\n-')
          .replace(/<\/li><\/ul>/g, '\n\n')
          .replace(/<\/?[^>]+(>|$)/g, "");
        body.push([el1, el2, el3]);
      }
      else {
        body.push(['', '', myDataRow.cells[0].innerHTML
          .replace('</span>', '\n\n')
          .replace(/<\/?[^>]+(>|$)/g, "")
        ]);
      }
    }
    let head = body[0];
    head.shift();
    head.unshift([""]);
    body.shift();
    // @ts-ignore
    head[2] = head[2].replace(/<\/?[^>]+(>|$)/g, "");

    doc.setFont('Roboto-Regular', 'normal');
    autoTable(doc, {
      head: [head],
      //html: "#table-results",
      startY: (doc as any).autoTable.previous.finalY + 15,
      body: body,
      headStyles: {
        font: 'Roboto-Bold',
        fontStyle: 'normal'
      },
      styles: {
        font: 'Roboto-Regular',
        fontStyle: 'normal'
      }
    });

    doc.setFont('Roboto-Regular', 'normal');
    doc.setFontSize(8);
    let y = (doc as any).autoTable.previous.finalY + 20;
    y = this._writeMultiLinesText(doc, this.formService.t(this.t, "disclaimer_title", false), y);
    this._writeMultiLinesText(doc, this.formService.t(this.t, "disclaimer_text", false), y);

    if (isMobileDevice()) {

      this.formService.sendPDF(form.form, doc.output('blob')).then((response: AxiosResponse<RequirementResults>) => {
        if (Capacitor.isNativePlatform()) {
          Browser.open({ url: `${ process.env.REACT_APP_API_URL }/` + response.data['link'] });
        }
        else {
          window.open(`${ process.env.REACT_APP_API_URL }/` + response.data['link'], '_blank', 'location=no');
        }
      });
    }
    else {
      doc.save("results.pdf");
    }
  }

  async exportWord(
    loadingWord,
    idSubmittedSubmission,
    language,
  ) {
    loadingWord(true)
    this.submissionService.exportSubmissionToWord(idSubmittedSubmission!, language).then((response: AxiosResponse<Blob>) => {
      FileUtils.downloadFile(response);
      loadingWord(false)

    })
  }

  //region UTILS
  private _getTodayDate() {
    const today = new Date();

    const month = today.getMonth() + 1;

    return `${ today.getDate() }/${ month < 10 ? '0' : '' }${ month }/${ today.getFullYear() }`;
  }

  private _createSummaryTable(
    table: any[],
    form: Form,
    history: number[],
    values: Record<string, any>
  ) {
    FormUtils.formatValue(
      this.t,
      this.formService,
      form,
      history,
      values,
      (id: number, question: FormQuestion, value: string) => {
        switch (question.type) {
          // case QuestionType.DROPDOWN:
          default:
            value = this.formService.t(this.t, value, false);
            break;
        }
        table.push([this.formService.t(this.t, question.question, false), value]);
      }
    );
  }

  private _writeMultiLinesText(doc: jsPDF, text: any, y: number) {
    const pageHeight = doc.internal.pageSize.height;
    const lines = doc.splitTextToSize(text, 175);

    for (const line of lines) {
      if (y > pageHeight - 20) {
        doc.addPage();
        y = 15;
      }

      doc.text(line, 13, y);
      y += doc.getTextDimensions(text).h;
    }

    return y;
  }
  //endregion

  //region PREVIOUS VERSIONS
  // private async exportWord() {
  //   const htmlResults = document.getElementById("htmlTemplate")
  //   if (htmlResults) {
  //     //DOCX
  //     try {
  //       const blob : Blob = await HTMLtoDOCX(await this.generateHtmlDoc(htmlResults), null)
  //
  //       const url = window.URL.createObjectURL(blob);
  //       const a = document.createElement("a");
  //       document.body.appendChild(a);
  //       a.href = url;
  //       a.download = "results.docx";
  //       a.click();
  //       document.body.removeChild(a);
  //     }
  //     catch (e) {
  //       console.log(e)
  //     }
  //
  //     // DOC
  //     // const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(this.generateHtmlDoc(htmlResults))
  //     // let fileDownload = document.createElement("a");
  //     // document.body.appendChild(fileDownload);
  //     // fileDownload.href = source;
  //     // fileDownload.download = 'results.doc';
  //     // fileDownload.click();
  //     // document.body.removeChild(fileDownload);
  //   }
  // }
  //endregion
}